import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type AuditRow = Pick<
  Tables<"changelog_entries">,
  | "id"
  | "date"
  | "title"
  | "description"
  | "source"
  | "actor_id"
  | "created_at"
  | "actor_type"
  | "actor_label"
  | "entity_table"
  | "changed_columns"
> & {
  profiles: { display_name: string | null } | null;
};

export interface AuditLogEntry {
  id: string;
  date: string;
  title: string;
  description: string;
  source: string;
  actor_id: string | null;
  created_at: string;
  actor_name: string | null;
  /** 'user' | 'system' | 'customer'; NULL on rows written before #980. */
  actor_type: string | null;
  actor_label: string | null;
  entity_table: string | null;
  changed_columns: string[] | null;
}

export type AuditActorType = "user" | "system" | "customer";

export interface AuditLogFilters {
  entityTable?: string;
  actorType?: AuditActorType;
}

export interface AuditLogPage {
  entries: AuditLogEntry[];
  /** True when at least one older row matches the filters (#1192). */
  hasMore: boolean;
}

/** Rows per Audit Log page (#1192). */
export const AUDIT_LOG_PAGE_SIZE = 100;

/**
 * Who or what made an audit-log change (#1192). Users show their name, other
 * actors their #980 label (e.g. customer:plan_wizard). Rows from before #980
 * have no actor_type: one with an actor_id was a signed-in user, one without
 * was the system. useAuditLog's actor filter maps them the same way.
 */
export function auditActorDisplay(
  entry: Pick<AuditLogEntry, "actor_type" | "actor_label" | "actor_name" | "actor_id">,
): { label: string; kind: AuditActorType | "legacy" } {
  switch (entry.actor_type) {
    case "user":
      return { label: entry.actor_name ?? entry.actor_label ?? "Unknown user", kind: "user" };
    case "system":
    case "customer":
      return { label: entry.actor_label ?? entry.actor_type, kind: entry.actor_type };
    default:
      return entry.actor_id
        ? { label: entry.actor_name ?? "Unknown user", kind: "user" }
        : { label: "system", kind: "legacy" };
  }
}

const KEY = "audit_log_entries";

/** One page of audit-log rows, newest first, matching `filters` (#1192). */
export function useAuditLog(filters: AuditLogFilters = {}, page = 0) {
  const entriesQuery = useQuery({
    queryKey: [KEY, filters, page],
    queryFn: async (): Promise<AuditLogPage> => {
      let q = supabase
        .from("changelog_entries")
        .select(
          "id, date, title, description, source, actor_id, created_at, actor_type, actor_label, entity_table, changed_columns, profiles:actor_id(display_name)",
        );
      if (filters.entityTable) q = q.eq("entity_table", filters.entityTable);
      // Pre-#980 rows have no actor_type; count them as auditActorDisplay shows them.
      if (filters.actorType === "user") {
        q = q.or("actor_type.eq.user,and(actor_type.is.null,actor_id.not.is.null)");
      } else if (filters.actorType === "system") {
        q = q.or("actor_type.eq.system,and(actor_type.is.null,actor_id.is.null)");
      } else if (filters.actorType) {
        q = q.eq("actor_type", filters.actorType);
      }

      // One row past the page tells us whether an older page exists, without
      // counting the whole (fast-growing) table on every page turn.
      const from = page * AUDIT_LOG_PAGE_SIZE;
      const { data, error } = await q
        .order("created_at", { ascending: false })
        .order("id", { ascending: false }) // tie-break so offsets are stable
        .range(from, from + AUDIT_LOG_PAGE_SIZE);
      if (error) {
        // An offset past the last row (rows deleted since the page was opened).
        if (error.code === "PGRST103") return { entries: [], hasMore: false };
        throw error;
      }

      const rows = (data ?? []) as unknown as AuditRow[];
      return {
        hasMore: rows.length > AUDIT_LOG_PAGE_SIZE,
        entries: rows.slice(0, AUDIT_LOG_PAGE_SIZE).map((row) => ({
          id: row.id,
          date: row.date,
          title: row.title,
          description: row.description,
          source: row.source,
          actor_id: row.actor_id,
          created_at: row.created_at,
          actor_name: row.profiles?.display_name ?? null,
          actor_type: row.actor_type,
          actor_label: row.actor_label,
          entity_table: row.entity_table,
          changed_columns: row.changed_columns,
        })),
      };
    },
    // Keep the current rows on screen while the next page loads, but not
    // across a filter change, where they'd be the wrong rows.
    placeholderData: (prev, prevQuery) =>
      JSON.stringify(prevQuery?.queryKey[1]) === JSON.stringify(filters) ? prev : undefined,
  });

  return { entriesQuery };
}

/** Every entity_table the caller can see in the audit log, for the table filter (#1192). */
export function useAuditLogTables() {
  return useQuery({
    queryKey: [KEY, "tables"],
    queryFn: async (): Promise<string[]> => {
      // The function ships in this PR's migration, so the generated types only
      // learn its name at the next types:sync after deploy.
      const { data, error } = await supabase.rpc("audit_log_entity_tables" as never);
      if (error) throw error;
      return ((data ?? []) as unknown as string[]);
    },
  });
}

export interface PlatformUpdate {
  id: string;
  category: "feature" | "fix";
  title: string;
  committed_at: string;
}

const PLATFORM_UPDATES_KEY = ["platform_updates"];

/**
 * The user-facing "What's New" feed — humanized product updates pulled from
 * GitHub by the platform-updates workflow. Read-only, newest first.
 */
export function usePlatformUpdates() {
  const updatesQuery = useQuery({
    queryKey: PLATFORM_UPDATES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_updates")
        .select("id, category, title, committed_at")
        .order("committed_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as PlatformUpdate[];
    },
  });

  return { updatesQuery };
}
